import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import type { ClaimPipeline } from "@/api/generated/client/models/ClaimPipeline";

export interface PipelineDraft {
  name: string;
  description: string;
  color: string;
}

interface PipelineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided the dialog is in edit mode, otherwise create mode. */
  pipeline?: ClaimPipeline | null;
  onSubmit: (draft: PipelineDraft) => Promise<void>;
  isSaving: boolean;
}

const DEFAULT_COLOR = "#3B82F6";

export function PipelineDialog({
  open,
  onOpenChange,
  pipeline,
  onSubmit,
  isSaving,
}: PipelineDialogProps) {
  const isEditing = Boolean(pipeline);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR);

  useEffect(() => {
    if (!open) return;
    setName(pipeline?.name ?? "");
    setDescription(pipeline?.description ?? "");
    setColor(pipeline?.color ?? DEFAULT_COLOR);
  }, [open, pipeline]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await onSubmit({ name: name.trim(), description, color });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Workflow" : "New Workflow"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the name, description, or color for this workflow."
              : "Create a workflow to group a set of claim statuses (e.g. Adjusting, Appraisal, Production)."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="pipeline-name">Workflow Name *</Label>
            <Input
              id="pipeline-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Appraisal"
              disabled={isSaving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pipeline-description">Description</Label>
            <Textarea
              id="pipeline-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              disabled={isSaving}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pipeline-color">Color</Label>
            <div className="flex items-center gap-2">
              <Input
                id="pipeline-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-20 cursor-pointer"
                disabled={isSaving}
              />
              <Input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder={DEFAULT_COLOR}
                className="flex-1"
                disabled={isSaving}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !name.trim()}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Save Changes" : "Create Workflow"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
